import { Box, Divider, HoverCard, Stack, Table, Text, UnstyledButton } from '@mantine/core'
import type { DetailedInvoiceRow, DistrictRequirementsModel, RequirementFulfillment } from '@/api/main/types.gen'
import DriveTypeBadge from '@/components/Scheduling/DriveBadge'
import {
  formatSlackPercent,
  fulfillmentForRequirement,
  recommendedCars,
  requirementSlack,
  sumBillableKm,
} from '@/utils/autoScheduleRequirements'
import { splitSelectedFirst } from '@/utils/autoScheduleDistricts'
import { useTranslation } from 'react-i18next'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import classes from './AutoScheduleSelection.module.css'
import { requirementStatsZIndex } from './autoScheduleLayers'

const GRID =
  'minmax(5rem, 1.2fr) minmax(3.5rem, auto) minmax(3.5rem, 0.8fr) minmax(4.5rem, 0.9fr) minmax(8rem, 1.5fr) minmax(5.5rem, 1fr)'

const STATS_CARD_CLOSE_DELAY = 100

const ROW_STYLE = {
  display: 'grid',
  gridTemplateColumns: GRID,
  gap: 'var(--mantine-spacing-sm)',
  alignItems: 'center',
  fontVariantNumeric: 'tabular-nums',
} as const

function ConstraintLines({ requirement }: { requirement: DistrictRequirementsModel }) {
  if (requirement.time_constraints.length === 0) {
    return null
  }
  return (
    <Stack gap={4}>
      {requirement.time_constraints.map((constraint, index) => (
        <Text key={`${requirement.id}-c-${index}`} size="xs">
          {constraint.weekdays.join(', ')} · {constraint.time_from}–{constraint.time_to}
        </Text>
      ))}
    </Stack>
  )
}

function RequirementStats({
  requirement,
  match,
  totals,
  slack,
}: {
  requirement: DistrictRequirementsModel
  match: ReturnType<typeof fulfillmentForRequirement>
  totals: ReturnType<typeof sumBillableKm>
  slack: number | undefined
}) {
  const { t } = useTranslation('scheduling')
  return (
    <Stack gap="sm" maw={600}>
      <Text fw={600}>
        {t('autoSchedule.requirement')} #{requirement.id}
      </Text>
      <ConstraintLines requirement={requirement} />
      <Text size="sm">{t('autoSchedule.requestedDrives', { count: requirement.requested_drives })}</Text>
      <Table fz="xs" withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('driveType')}</Table.Th>
            <Table.Th ta="right">{t('driven')}</Table.Th>
            <Table.Th ta="right">{t('autoSchedule.plannedKm')}</Table.Th>
            <Table.Th ta="right">{t('autoSchedule.orderedKm')}</Table.Th>
            <Table.Th ta="right">{t('autoSchedule.remainingKm')}</Table.Th>
            <Table.Th ta="right">{t('autoSchedule.slack')}</Table.Th>
            <Table.Th ta="right">{t('autoSchedule.dataKm')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {requirement.drive_type.map(type => {
            const data = match?.billable_km[type]
            if (!data) {
              return null
            }
            const remainingKm = data.ordered_km - data.driven_km - data.planned_km
            const typeSlack = requirementSlack(
              {
                drivenKm: data.driven_km,
                plannedKm: data.planned_km,
                orderedKm: data.ordered_km,
                fulfillableKm: data.fulfillable_km,
                remainingKm,
              },
              match?.requirements_time.remaining_time,
              match?.requirements_time.total_time
            )
            return (
              <Table.Tr key={type}>
                <Table.Td>{t(`driveTypes.${type}`)}</Table.Td>
                <Table.Td ta="right">{data.driven_km.toFixed(1)}</Table.Td>
                <Table.Td ta="right">{data.planned_km.toFixed(1)}</Table.Td>
                <Table.Td ta="right">{data.ordered_km.toFixed(1)}</Table.Td>
                <Table.Td ta="right">{remainingKm.toFixed(1)}</Table.Td>
                <Table.Td ta="right">{formatSlackPercent(typeSlack)}</Table.Td>
                <Table.Td ta="right">{data.fulfillable_km.toFixed(1)}</Table.Td>
              </Table.Tr>
            )
          })}
          <Table.Tr>
            <Table.Td>{t('autoSchedule.total')}</Table.Td>
            <Table.Td ta="right">{totals.drivenKm.toFixed(1)}</Table.Td>
            <Table.Td ta="right">{totals.plannedKm.toFixed(1)}</Table.Td>
            <Table.Td ta="right">{totals.orderedKm.toFixed(1)}</Table.Td>
            <Table.Td ta="right">{totals.remainingKm.toFixed(1)}</Table.Td>
            <Table.Td ta="right">{formatSlackPercent(slack)}</Table.Td>
            <Table.Td ta="right">{totals.fulfillableKm.toFixed(1)}</Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </Stack>
  )
}

const RequirementRow = memo(function RequirementRow({
  requirement,
  fulfillment,
  overhead,
  totalOverhead,
  availableVehicles,
  hovered,
  selected,
  disabled,
  showCard,
  onToggle,
  onHover,
  onCard,
  onCardClose,
}: {
  requirement: DistrictRequirementsModel
  fulfillment: RequirementFulfillment[]
  overhead: number | undefined
  totalOverhead: number
  availableVehicles: number
  hovered: boolean
  selected: boolean
  disabled: boolean
  showCard: boolean
  onToggle: (districtId: number) => void
  onHover: (districtId: number | undefined) => void
  onCard: (requirementId: number) => void
  onCardClose: (requirementId: number) => void
}) {
  const match = fulfillmentForRequirement(fulfillment, requirement.id)
  const totals = sumBillableKm(requirement.drive_type, match?.billable_km)
  const slack = requirementSlack(totals, match?.requirements_time.remaining_time, match?.requirements_time.total_time)
  const cars = recommendedCars(overhead, totalOverhead, availableVehicles)
  const slackColor = slack === undefined ? undefined : slack > 0 ? 'red.6' : 'teal.5'
  const label = `${requirement.district_code}–${requirement.subdistrict_code}`

  const button = (
    <UnstyledButton
      onClick={disabled ? undefined : () => onToggle(requirement.district_id)}
      onMouseEnter={() => {
        if (!disabled) {
          onCard(requirement.id)
        }
        onHover(requirement.district_id)
      }}
      onMouseLeave={() => onHover(undefined)}
      onFocus={() => onHover(requirement.district_id)}
      onBlur={() => onHover(undefined)}
      data-requirement-row={requirement.id}
      w="100%"
      px="sm"
      py="xs"
      aria-pressed={disabled ? undefined : selected}
      aria-disabled={disabled || undefined}
      aria-label={label}
      className={clsx(
        classes.row,
        hovered && classes.hovered,
        selected && classes.selected,
        disabled && classes.disabled
      )}
      style={{ ...ROW_STYLE, cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      <Text size="xs" truncate>
        {label}
      </Text>
      <DriveTypeBadge driveType={requirement.drive_type} />
      <Text size="xs" ta="right" c={slackColor}>
        {formatSlackPercent(slack)}
      </Text>
      <Text size="xs" ta="right">
        {totals.fulfillableKm.toFixed(1)}
      </Text>
      <Text size="xs" ta="right">
        {overhead === undefined ? '—' : overhead.toFixed(1)}
      </Text>
      <Text size="xs" ta="right">
        {cars === undefined ? '—' : cars.toFixed(1)}
      </Text>
    </UnstyledButton>
  )

  if (!showCard) {
    return button
  }

  return (
    <HoverCard
      initiallyOpened
      position="left"
      withArrow
      shadow="md"
      withinPortal
      portalProps={{ target: document.body }}
      floatingStrategy="fixed"
      middlewares={{ flip: false, shift: { crossAxis: false }, inline: false }}
      zIndex={requirementStatsZIndex}
      openDelay={0}
      closeDelay={STATS_CARD_CLOSE_DELAY}
      onClose={() => onCardClose(requirement.id)}
    >
      <HoverCard.Target>{button}</HoverCard.Target>
      <HoverCard.Dropdown data-requirement-stats>
        <RequirementStats requirement={requirement} match={match} totals={totals} slack={slack} />
      </HoverCard.Dropdown>
    </HoverCard>
  )
})

export function RequirementsTable({
  relevant,
  irrelevant,
  fulfillment,
  invoiceRows,
  availableVehicles,
  selectedIds,
  hoveredId,
  onToggle,
  onHover,
}: {
  relevant: DistrictRequirementsModel[]
  irrelevant: DistrictRequirementsModel[]
  fulfillment: RequirementFulfillment[]
  invoiceRows: DetailedInvoiceRow[]
  availableVehicles: number
  selectedIds: number[]
  hoveredId: number | undefined
  onToggle: (districtId: number) => void
  onHover: (districtId: number | undefined) => void
}) {
  const { t } = useTranslation('scheduling')
  const [pointerCardId, setPointerCardId] = useState<number>()
  const closeCard = useCallback((requirementId: number) => {
    setPointerCardId(current => (current === requirementId ? undefined : current))
  }, [])
  useEffect(() => {
    if (pointerCardId === undefined) {
      return
    }
    const dismiss = (event: Event) => {
      const target = event.target
      if (!(target instanceof Element)) {
        setPointerCardId(undefined)
        return
      }
      if (target.closest('[data-requirement-stats]')) {
        return
      }
      if (target.closest(`[data-requirement-row="${pointerCardId}"]`)) {
        return
      }
      setPointerCardId(undefined)
    }
    document.addEventListener('pointerdown', dismiss, true)
    return () => document.removeEventListener('pointerdown', dismiss, true)
  }, [pointerCardId])
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const overheadByRequirementId = useMemo(() => {
    const overhead = new Map<number, number>()
    for (const row of invoiceRows) {
      overhead.set(row.master_id, row.km_stat_ordered - row.km_stat)
    }
    return overhead
  }, [invoiceRows])
  const totalOverhead = useMemo(() => {
    let sum = 0
    for (const requirement of relevant) {
      sum += overheadByRequirementId.get(requirement.id) ?? 0
    }
    return sum
  }, [relevant, overheadByRequirementId])
  const { selected, rest } = splitSelectedFirst(relevant, requirement => selectedSet.has(requirement.district_id))

  const renderRow = (requirement: DistrictRequirementsModel, disabled: boolean) => (
    <RequirementRow
      key={requirement.id}
      requirement={requirement}
      fulfillment={fulfillment}
      overhead={overheadByRequirementId.get(requirement.id)}
      totalOverhead={totalOverhead}
      availableVehicles={availableVehicles}
      hovered={hoveredId === requirement.district_id}
      selected={!disabled && selectedSet.has(requirement.district_id)}
      disabled={disabled}
      showCard={!disabled && pointerCardId === requirement.id}
      onToggle={onToggle}
      onHover={onHover}
      onCard={setPointerCardId}
      onCardClose={closeCard}
    />
  )

  return (
    <Box style={{ minHeight: 0, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        style={{
          overflowX: 'auto',
          overflowY: 'hidden',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box miw="42rem" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Box
            px="sm"
            pb="xs"
            style={{
              display: 'grid',
              gridTemplateColumns: GRID,
              gap: 'var(--mantine-spacing-sm)',
              fontVariantNumeric: 'tabular-nums',
              flexShrink: 0,
            }}
          >
            <Text size="xs" c="dimmed" />
            <Text size="xs" c="dimmed">
              {t('autoSchedule.type')}
            </Text>
            <Text size="xs" c="dimmed" ta="right" lh={1.3}>
              {t('autoSchedule.slack')}
            </Text>
            <Text size="xs" c="dimmed" ta="right" lh={1.3}>
              {t('autoSchedule.dataKm')}
            </Text>
            <Text size="xs" c="dimmed" ta="right" lh={1.3}>
              {t('autoSchedule.unbilledOverhead')}
            </Text>
            <Text size="xs" c="dimmed" ta="right" lh={1.3}>
              {t('autoSchedule.recommendedCars')}
            </Text>
          </Box>
          <Box style={{ overflowY: 'auto', minHeight: 0, flex: 1 }}>
            {selected.map(requirement => renderRow(requirement, false))}
            {selected.length > 0 && rest.length > 0 ? <Divider my={6} /> : null}
            {rest.map(requirement => renderRow(requirement, false))}
            {irrelevant.length > 0 && (selected.length > 0 || rest.length > 0) ? <Divider my={6} /> : null}
            {irrelevant.map(requirement => renderRow(requirement, true))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
